import { resolveRenderMode } from './formats';
import { IrdGroupEntry, IrdOrderEntry } from './order';
import { IrdPropertyBuilder, IrdPropertyDeclaration } from './property-builder';
import {
  IRD_DEFAULT_EMPTY_MARKER,
  IRD_PRIMARY_KEY_DEFAULT,
  IrdAttributeProvenance,
  IrdPropertyPresentation,
  IrdResolvedGroup,
  IrdResolvedMetadata,
  IrdResolvedProperty,
  IrdSchemaFact,
} from './resolved';

const UPPER_SNAKE_BOUNDARY = /([a-z0-9])([A-Z])/g;

/**
 * One layer of a metadata chain — the builder half of ADR-0001's builder/value-object split.
 *
 * A chain is how metadata is **written**: layers wrap their predecessor by reference — schema,
 * entity level, context level, and any further level a developer needs. It is never how metadata
 * is read. A layer exposes nothing to read; {@link build} folds the chain into an
 * {@link IrdResolvedMetadata}, which is what a component takes. There is deliberately no path from
 * that result back to the unfolded declarations, and no way to declare anything on it.
 *
 * The fold happens in {@link build}:
 *
 * - **attributes resolve with the most derived layer winning** — the layer furthest from the
 *   schema. That is what lets a context refine a label or a format the entity level set, and it
 *   is why inserting a generated layer *beneath* an existing declaration needs no special rule;
 * - **set operations apply parent first** — `exclude` is additive and final, `only` is a standing
 *   rule, and `order` operations compose in chain order.
 *
 * Diagrams of the chain, the resolution rules and the view's states: `docs/metadata/metadata.md`.
 */
export abstract class IrdMetadataLayer<T, C extends string = string> {
  /** Names this layer in `explain()` output, and types the context of what {@link build} returns. */
  protected abstract readonly layerName: C;

  protected readonly ownAttributes = new Map<string, IrdPropertyPresentation>();
  protected readonly ownExcluded = new Set<string>();
  protected ownOnly?: ReadonlySet<string>;
  protected ownOrder?: readonly IrdOrderEntry[];
  protected ownTranslationKeyPrefix?: string;
  protected ownEntityName?: string;
  /** Set by the entity level only: the key is a schema fact. */
  protected ownPrimaryKey?: string & keyof T;
  protected ownShowPrimaryKey?: boolean;
  protected ownEmptyMarker?: string;
  /** What an absent value reads as in this context when no layer declares a marker. */
  protected readonly defaultEmptyMarker: string = IRD_DEFAULT_EMPTY_MARKER;
  /**
   * Properties the pre-chain declaration marked `showInUi: false`. Held rather than applied: the
   * flag was only ever written with a table or a form in mind, so reading it as an entity-wide
   * exclusion is what hid `description` and `mapIndex` from the detail view in the first place.
   */
  protected legacyHiddenKeys: ReadonlySet<string> = new Set<string>();

  protected constructor(protected readonly parent?: IrdMetadataLayer<T>) {}

  /**
   * Folds the whole chain into the resolved read model. The result is a snapshot: declaring more
   * on this builder afterwards does not change a model already built.
   */
  public build(): IrdResolvedMetadata<T, C> {
    const chain = this.chainFromRoot();
    const facts = new Map<string, IrdSchemaFact>();
    const sources = new Map<string, Map<string, string>>();
    const attributes = new Map<string, IrdPropertyPresentation>();

    this.collectSchemaFacts(chain, facts, sources);
    this.collectAttributes(chain, attributes, sources);

    const primaryKey = this.resolvePrimaryKey(chain);
    const excludedAt = this.collectExclusions(chain, facts);
    // An identifier means nothing to a reader, so it is hidden unless a view asks for it: by naming
    // it in `only`, or by `showPrimaryKey()`, whose most derived call wins over that.
    const namedByOnly = this.foldOnly(chain)?.keys.has(primaryKey) ?? false;
    const showPrimaryKey = chain.reduce((show, layer) => layer.ownShowPrimaryKey ?? show, namedByOnly);
    if (!showPrimaryKey && facts.has(primaryKey) && !excludedAt.has(primaryKey)) {
      excludedAt.set(primaryKey, IRD_PRIMARY_KEY_DEFAULT);
    }
    const visible = [...facts.keys()].filter((key) => !excludedAt.has(key));
    const sequence = this.foldOrder(chain, visible, facts);
    const emptyMarker = chain.reduce((marker, layer) => layer.ownEmptyMarker ?? marker, this.defaultEmptyMarker);

    return this.toResolved(
      primaryKey,
      this.toGroups(sequence, facts, attributes, this.resolvePrefix(chain), emptyMarker),
      chain.reduce<string | undefined>((name, layer) => layer.ownEntityName ?? name, undefined),
      this.toProvenance(facts, sources, excludedAt),
    );
  }

  /** Wraps the folded parts. A context with facts of its own about the whole view returns a subclass. */
  protected toResolved(
    primaryKey: string & keyof T,
    groups: readonly IrdResolvedGroup<T>[],
    entityNameKey: string | undefined,
    provenance: ReadonlyMap<string, IrdAttributeProvenance>,
  ): IrdResolvedMetadata<T, C> {
    return new IrdResolvedMetadata<T, C>(this.layerName, primaryKey, groups, entityNameKey, provenance);
  }

  /**
   * Shows the primary key property, which every view hides by default, in this layer and the layers
   * built on it; `showPrimaryKey(false)` hides it again. The most derived layer that says so wins,
   * also over an `only` that names the key, and an `exclude` still wins over it.
   */
  public showPrimaryKey(show = true): this {
    this.ownShowPrimaryKey = show;
    return this;
  }

  /**
   * Removes properties from this layer downwards. Additive and final: no deeper layer can put
   * them back, which is what makes an entity-level exclusion of a technical identifier hold in
   * every view built on it.
   */
  public exclude(...keys: (string & keyof T)[]): this {
    keys.forEach((key) => this.ownExcluded.add(key));
    return this;
  }

  /**
   * Narrows this layer to the named properties. A standing rule, not a one-time filter: a
   * property the schema gains afterwards is not in the named set, so it stays out.
   */
  public only(...keys: (string & keyof T)[]): this {
    this.ownOnly = new Set<string>(keys);
    return this;
  }

  /**
   * Declares the order, and the grouping, of this layer. Keys named here take the declared
   * positions; anything not named falls in behind in its inherited order. Entries may interleave
   * loose properties and {@link group} declarations freely.
   */
  public order(...entries: IrdOrderEntry[]): this {
    this.ownOrder = entries;
    return this;
  }

  /**
   * Overrides presentation attributes of one property, declared on a builder:
   * `.property('name', (p) => p.emptyMarker('n/a'))`. Only the attributes called change; a second
   * call for the same property merges into the first. A context with attributes of its own hands
   * its own builder.
   */
  public property<K extends string & keyof T>(key: K, declare: IrdPropertyDeclaration<IrdPropertyBuilder<T[K], C>>): this {
    return this.declareProperty(key, declare, new IrdPropertyBuilder<T[K], C>());
  }

  /** Runs a declaration on the builder a context hands out and merges what it declared. */
  protected declareProperty<B extends IrdPropertyBuilder<never>>(key: string, declare: IrdPropertyDeclaration<B>, builder: B): this {
    declare(builder);
    this.ownAttributes.set(key, { ...this.ownAttributes.get(key), ...builder.toPresentation() });
    return this;
  }

  /**
   * What an absent value reads as in every property of this layer and the layers built on it, e.g.
   * `'n/a'` for a whole table. A property's own `emptyMarker` still wins.
   */
  public emptyMarker(marker: string): this {
    this.ownEmptyMarker = marker;
    return this;
  }

  /** Names the entity for texts about the whole record, as a translation key. */
  public entityName(translationKey: string): this {
    this.ownEntityName = translationKey;
    return this;
  }

  /** Overrides the translation-key prefix inherited from the entity level. */
  public translationKeyPrefix(prefix: string): this {
    this.ownTranslationKeyPrefix = prefix;
    return this;
  }

  /** The schema facts this layer contributes. Only the entity level has any. */
  protected schemaFacts(): readonly IrdSchemaFact[] {
    return [];
  }

  /**
   * Applies the inherited legacy `showInUi` flag as an exclusion on this layer. Only the form
   * context calls it, so the edit dialog keeps behaving as it does today; the detail and table
   * contexts declare their exclusions instead.
   */
  protected excludeLegacyHidden(): this {
    for (let layer: IrdMetadataLayer<T> | undefined = this.parent; layer; layer = layer.parent) {
      layer.legacyHiddenKeys.forEach((key) => this.ownExcluded.add(key));
    }
    return this;
  }

  /** A chain must root in an entity level: it is the only layer that sets the key. */
  private resolvePrimaryKey(chain: IrdMetadataLayer<T>[]): string & keyof T {
    const primaryKey = chain.find((layer) => layer.ownPrimaryKey !== undefined)?.ownPrimaryKey;
    if (primaryKey === undefined) {
      throw new Error(`${this.layerName}: no layer of this chain declares a primary key.`);
    }
    return primaryKey;
  }

  private chainFromRoot(): IrdMetadataLayer<T>[] {
    const chain: IrdMetadataLayer<T>[] = [this];
    let ancestor = this.parent;
    while (ancestor) {
      chain.unshift(ancestor);
      ancestor = ancestor.parent;
    }
    return chain;
  }

  private collectSchemaFacts(
    chain: IrdMetadataLayer<T>[],
    facts: Map<string, IrdSchemaFact>,
    sources: Map<string, Map<string, string>>,
  ): void {
    for (const layer of chain) {
      for (const fact of layer.schemaFacts()) {
        facts.set(fact.key, fact);
        const source = new Map<string, string>();
        for (const attribute of ['type', 'array', 'translationKey', ...(fact.capabilities ? ['capabilities'] : [])]) {
          source.set(attribute, layer.layerName);
        }
        sources.set(fact.key, source);
      }
    }
  }

  private collectAttributes(
    chain: IrdMetadataLayer<T>[],
    attributes: Map<string, IrdPropertyPresentation>,
    sources: Map<string, Map<string, string>>,
  ): void {
    for (const layer of chain) {
      for (const [key, declared] of layer.ownAttributes) {
        // An attribute left `undefined` says nothing, so it must not overwrite what a parent declared.
        const declared_ = Object.entries(declared).filter(([, value]) => value !== undefined);
        const source = sources.get(key) ?? new Map<string, string>();
        for (const [attribute] of declared_) {
          source.set(attribute, layer.layerName);
        }
        attributes.set(key, { ...attributes.get(key), ...Object.fromEntries(declared_) });
        sources.set(key, source);
      }
    }
  }

  /**
   * `exclude` is additive and final: the first layer to remove a property wins, and no deeper
   * layer can put it back. `only` is a standing rule rather than a one-time filter — a property
   * the schema gains afterwards is outside the named set, so it stays out.
   */
  private collectExclusions(chain: IrdMetadataLayer<T>[], facts: Map<string, IrdSchemaFact>): Map<string, string> {
    const excludedAt = new Map<string, string>();
    for (const layer of chain) {
      for (const key of layer.ownExcluded) {
        if (!excludedAt.has(key)) {
          excludedAt.set(key, layer.layerName);
        }
      }
    }

    const only = this.foldOnly(chain);
    if (only) {
      for (const key of facts.keys()) {
        if (!only.keys.has(key) && !excludedAt.has(key)) {
          excludedAt.set(key, only.layerName);
        }
      }
    }
    return excludedAt;
  }

  /**
   * The layers' `only` sets intersect: a deeper layer can narrow the named set but never widen it,
   * which is what makes `only` a standing rule rather than a filter applied once.
   */
  private foldOnly(chain: IrdMetadataLayer<T>[]): { keys: Set<string>; layerName: string } | undefined {
    let keys: Set<string> | undefined;
    let layerName = '';

    for (const layer of chain) {
      if (layer.ownOnly) {
        const narrowed = layer.ownOnly;
        keys = keys ? new Set([...keys].filter((key) => narrowed.has(key))) : new Set(narrowed);
        layerName = layer.layerName;
      }
    }
    return keys ? { keys, layerName } : undefined;
  }

  private foldOrder(chain: IrdMetadataLayer<T>[], visible: string[], facts: Map<string, IrdSchemaFact>): IrdOrderEntry[] {
    let sequence: IrdOrderEntry[] = visible.slice();
    for (const layer of chain) {
      if (layer.ownOrder) {
        this.assertOrderIsDeclarable(layer.ownOrder, facts, layer.layerName);
        sequence = applyOrder(sequence, layer.ownOrder);
      }
    }
    return pruneToVisible(sequence, new Set(visible));
  }

  /**
   * A key in an `order(...)` that no layer's schema knows is a typo, and a typo that silently
   * drops a property is exactly the quiet failure this chain exists to remove — so it throws.
   * A key that *is* known but was excluded is not an error: exclusion wins and the entry is
   * simply dropped.
   */
  private assertOrderIsDeclarable(order: readonly IrdOrderEntry[], facts: Map<string, IrdSchemaFact>, layerName: string): void {
    for (const entry of order) {
      if (entry instanceof IrdGroupEntry) {
        if (facts.has(entry.key)) {
          throw new Error(`${layerName}: '${entry.key}' is declared both as a group and as a property — the order list is one namespace.`);
        }
        for (const key of entry.propertyKeys) {
          assertKnown(key, facts, layerName);
        }
      } else {
        assertKnown(entry, facts, layerName);
      }
    }
  }

  private resolvePrefix(chain: IrdMetadataLayer<T>[]): string {
    let prefix = '';
    for (const layer of chain) {
      if (layer.ownTranslationKeyPrefix) {
        prefix = layer.ownTranslationKeyPrefix;
      }
    }
    return prefix;
  }

  private toGroups(
    sequence: IrdOrderEntry[],
    facts: Map<string, IrdSchemaFact>,
    attributes: Map<string, IrdPropertyPresentation>,
    prefix: string,
    emptyMarker: string,
  ): readonly IrdResolvedGroup<T>[] {
    const groups: IrdResolvedGroup<T>[] = [];
    let loose: IrdResolvedProperty<T>[] = [];

    const flush = (): void => {
      if (loose.length > 0) {
        groups.push({ key: '', anonymous: true, collapsible: false, collapsed: false, properties: loose });
        loose = [];
      }
    };

    for (const entry of sequence) {
      if (entry instanceof IrdGroupEntry) {
        flush();
        groups.push({
          key: entry.key,
          anonymous: false,
          translationKey: groupTranslationKey(prefix, entry.key),
          collapsible: entry.canCollapse,
          collapsed: entry.initiallyCollapsed,
          properties: entry.propertyKeys.map((key) => resolveProperty<T>(key, facts, attributes, emptyMarker)),
        });
      } else {
        loose.push(resolveProperty<T>(entry, facts, attributes, emptyMarker));
      }
    }
    flush();
    return groups;
  }

  private toProvenance(
    facts: Map<string, IrdSchemaFact>,
    sources: Map<string, Map<string, string>>,
    excludedAt: Map<string, string>,
  ): ReadonlyMap<string, IrdAttributeProvenance> {
    const provenance = new Map<string, IrdAttributeProvenance>();
    for (const key of facts.keys()) {
      provenance.set(key, {
        key,
        excludedAt: excludedAt.get(key),
        attributes: Object.fromEntries(sources.get(key) ?? []),
      });
    }
    return provenance;
  }
}

function assertKnown(key: string, facts: Map<string, IrdSchemaFact>, layerName: string): void {
  if (!facts.has(key)) {
    throw new Error(`${layerName}: order names '${key}', which no layer of this chain declares.`);
  }
}

/**
 * Applies one layer's order to the sequence it inherited. Keys the layer names take the declared
 * positions; everything it does not name falls in behind, keeping its inherited relative order.
 * A redeclared group keeps the inherited members it does not name, behind the ones it does.
 */
function applyOrder(previous: IrdOrderEntry[], order: readonly IrdOrderEntry[]): IrdOrderEntry[] {
  const claimed = new Set<string>();
  const redeclaredGroups = new Set<string>();
  for (const entry of order) {
    if (entry instanceof IrdGroupEntry) {
      redeclaredGroups.add(entry.key);
      entry.propertyKeys.forEach((key) => claimed.add(key));
    } else {
      claimed.add(entry);
    }
  }

  const tail: IrdOrderEntry[] = [];
  const unclaimedByGroup = new Map<string, string[]>();
  for (const entry of previous) {
    if (entry instanceof IrdGroupEntry) {
      const kept = entry.propertyKeys.filter((key) => !claimed.has(key));
      if (redeclaredGroups.has(entry.key)) {
        unclaimedByGroup.set(entry.key, kept);
      } else if (kept.length > 0) {
        tail.push(regroup(entry, kept));
      }
    } else if (!claimed.has(entry)) {
      tail.push(entry);
    }
  }

  const head = order.map((entry) => {
    if (!(entry instanceof IrdGroupEntry)) {
      return entry;
    }
    const unclaimed = unclaimedByGroup.get(entry.key) ?? [];
    return unclaimed.length > 0 ? regroup(entry, [...entry.propertyKeys, ...unclaimed]) : entry;
  });
  return [...head, ...tail];
}

/** Drops entries for properties an exclusion removed, and the groups that empties. */
function pruneToVisible(sequence: IrdOrderEntry[], visible: Set<string>): IrdOrderEntry[] {
  const pruned: IrdOrderEntry[] = [];
  for (const entry of sequence) {
    if (entry instanceof IrdGroupEntry) {
      const kept = entry.propertyKeys.filter((key) => visible.has(key));
      if (kept.length > 0) {
        pruned.push(regroup(entry, kept));
      }
    } else if (visible.has(entry)) {
      pruned.push(entry);
    }
  }
  return pruned;
}

function regroup(entry: IrdGroupEntry, propertyKeys: readonly string[]): IrdGroupEntry {
  const copy = new IrdGroupEntry(entry.key, propertyKeys);
  if (entry.initiallyCollapsed) {
    return copy.collapsed();
  }
  return entry.canCollapse ? copy.collapsible() : copy;
}

function resolveProperty<T>(
  key: string,
  facts: Map<string, IrdSchemaFact>,
  attributes: Map<string, IrdPropertyPresentation>,
  emptyMarker: string,
): IrdResolvedProperty<T> {
  const fact = facts.get(key);
  if (!fact) {
    throw new Error(`No layer of this chain declares the property '${key}'.`);
  }
  const presentation = attributes.get(key) ?? {};
  return {
    key: key as string & keyof T,
    type: fact.type,
    array: fact.array,
    translationKey: presentation.translationKey ?? fact.translationKey,
    format: presentation.format,
    renderAs: resolveRenderMode(fact.type, presentation.format, presentation.renderAs),
    emptyMarker: presentation.emptyMarker ?? emptyMarker,
    capabilities: fact.capabilities,
    daoName: fact.daoName,
    validators: fact.validators,
    allowedValues: fact.allowedValues,
    sortBy: resolveSortBy(key, fact, presentation),
    truncate: presentation.truncate ?? false,
    maxWidth: presentation.maxWidth ?? '',
    editable: resolveEditable(fact, presentation),
  };
}

/** Capability bounds intent here too: a write the API refuses is never offered, whatever a view says. */
function resolveEditable(fact: IrdSchemaFact, presentation: IrdPropertyPresentation): boolean {
  return fact.capabilities?.editable !== false && (presentation.editable ?? fact.editable);
}

/**
 * Capability bounds intent: a sort the API refuses is not offered, an unknown one is (ADR-0003).
 * An empty sort field or backend name counts as unset, as the legacy builder treats it.
 */
function resolveSortBy(key: string, fact: IrdSchemaFact, presentation: IrdPropertyPresentation): string | undefined {
  if (!presentation.sortable || fact.capabilities?.sortable === false) {
    return undefined;
  }
  return presentation.sortField || fact.daoName || key;
}

/** Group headings follow the same prefix convention as property labels. */
function groupTranslationKey(prefix: string, key: string): string {
  const name = key.replace(UPPER_SNAKE_BOUNDARY, '$1_$2').toUpperCase();
  return prefix ? `${prefix}.${name}` : name;
}
